if (!customElements.get('product-form')) {
  class ProductForm extends HTMLElement {
    constructor() {
      super();
      if (this.hasChildNodes()) this.init();
    }

    init() {
      this.form = this.querySelector('.js-product-form');
      if (this.form) {
        this.cartDrawer = document.querySelector('cart-drawer');

        if (theme.settings.afterAtc !== 'no-js') {
          this.form.addEventListener('submit', this.handleSubmit.bind(this));
        }
      }
    }

    /**
     * Collect power source / endcap companions from the PDP (main form only).
     * @param {number} quantity
     * @returns {Array<{id: number, quantity: number, properties: Object}>}
     */
    getCompanionItems(quantity) {
      if (!this.form?.classList.contains('js-product-form-main')) return [];

      const productSection = this.closest('.js-product');
      if (!productSection) return [];

      const companions = [];
      const qty = Number.isFinite(quantity) && quantity > 0 ? quantity : 1;

      const powerEl = productSection.querySelector('power-packs');
      const packVariantId = powerEl?.getSelectedPackVariantId?.();
      if (packVariantId) {
        companions.push({
          id: Number(packVariantId),
          quantity: qty,
          properties: {
            _added_as: 'Power source'
          }
        });
      }

      const endcapEl = productSection.querySelector('endcaps-selector');
      const endcapVariantId = endcapEl?.getSelectedEndcapVariantId?.();
      if (endcapVariantId) {
        companions.push({
          id: Number(endcapVariantId),
          quantity: qty,
          properties: {
            _added_as: 'Endcap or Connectable'
          }
        });
      }

      return companions;
    }

    /**
     * Next per-add configuration sequence number, scanning existing cart items.
     * Reads `Set-<n>` off each line's `_Configuration` property and returns max + 1.
     * @param {Array<{properties?: Object}>} cartItems - Items from the current cart (/cart.js).
     * @returns {number}
     */
    getNextSetNumber(cartItems) {
      const numbers = (cartItems || []).map((item) => {
        const label = item?.properties?._Configuration;
        const match = typeof label === 'string' ? label.match(/^Set-(\d+)/) : null;
        return match ? Number.parseInt(match[1], 10) : 0;
      });
      return Math.max(0, ...numbers) + 1;
    }

    /**
     * Build the `_Configuration` label for the light + its accessories.
     * Underscore-prefixed so Shopify hides it from customers at checkout and in
     * notifications; it still shows under each line on the admin order page.
     * Reads the live selected variant from the variant-picker JSON in this product
     * section, matched against the submitted variant id.
     * @param {number} setNumber - Per-add sequence number.
     * @param {FormData} formData
     * @returns {string} e.g. `Set-1 · Connectable Cut to Length Rope Light · Blue / 10m`
     */
    buildConfigurationLabel(setNumber, formData) {
      const prefix = `Set-${setNumber}`;
      const productSection = this.closest('.js-product');

      let variant = null;
      const dataEl = productSection?.querySelector('variant-picker [type="application/json"]');
      if (dataEl) {
        try {
          variant = JSON.parse(dataEl.textContent);
        } catch (e) {
          variant = null;
        }
      }

      const selectedId = Number(formData.get('id'));
      const variantMatches = variant && Number(variant.id) === selectedId;
      const variantTitle = variantMatches
        && typeof variant.title === 'string'
        && variant.title !== 'Default Title'
        ? variant.title
        : '';

      // Product title: derive from the variant name, falling back to the DOM title.
      let productTitle = '';
      if (variantMatches && typeof variant.name === 'string') {
        productTitle = variantTitle && variant.name.endsWith(` - ${variantTitle}`)
          ? variant.name.slice(0, variant.name.length - (variantTitle.length + 3))
          : variant.name;
      }
      if (!productTitle) {
        const titleEl = productSection?.querySelector('.product-title');
        if (titleEl) {
          productTitle = (titleEl.childNodes[0]?.textContent || titleEl.textContent || '').trim();
        }
      }

      const parts = [prefix];
      if (productTitle) parts.push(productTitle);
      if (variantTitle) parts.push(variantTitle);
      return parts.join(' · ');
    }

    /**
     * Build cart/add payload items from the product form + companions.
     * When the add includes at least one companion (power source / endcap) AND this
     * product is on the configuration-label allowlist (section setting, rendered as
     * data-configuration-label on <product-form>), the light and every companion get a
     * shared hidden `_Configuration` label so fulfilment can sort each configuration.
     * Otherwise the payload is identical to a plain add.
     * @param {FormData} formData
     * @param {number} setNumber - Per-add sequence number for the `_Configuration` label.
     * @returns {{ items: Array, mainVariantId: number }}
     */
    buildCartItems(formData, setNumber) {
      const mainVariantId = Number(formData.get('id'));
      const quantity = Number.parseInt(String(formData.get('quantity') || '1'), 10);
      const qty = Number.isFinite(quantity) && quantity > 0 ? quantity : 1;

      const properties = {};
      formData.forEach((value, key) => {
        const match = key.match(/^properties\[(.+)\]$/);
        if (match) properties[match[1]] = value;
      });

      const companions = this.getCompanionItems(qty);

      // Only label a configuration that has companions to link, on allowlisted products.
      if (companions.length && this.dataset.configurationLabel === 'true') {
        const label = this.buildConfigurationLabel(setNumber, formData);
        properties._Configuration = label;
        companions.forEach((companion) => {
          companion.properties = { ...companion.properties, _Configuration: label };
        });
      }

      const mainItem = {
        id: mainVariantId,
        quantity: qty
      };
      if (Object.keys(properties).length) {
        mainItem.properties = properties;
      }

      const sellingPlan = formData.get('selling_plan');
      if (sellingPlan) {
        mainItem.selling_plan = sellingPlan;
      }

      return {
        items: [mainItem, ...companions],
        mainVariantId
      };
    }

    /**
     * Handles submission of the product form.
     * @param {object} evt - Event object.
     */
    async handleSubmit(evt) {
      if (evt.target.id === 'product-signup_form') return;

      evt.preventDefault();

      this.submitBtn = this.querySelector('[name="add"]');

      if (this.submitBtn.getAttribute('aria-disabled') === 'true') return;

      // Belt-and-suspenders: endcap is required whenever the selector is shown.
      const productSection = this.closest('.js-product');
      const endcapEl = productSection?.querySelector('endcaps-selector');
      if (endcapEl?.isConnected && typeof endcapEl.getSelectedEndcapVariantId === 'function') {
        if (!endcapEl.getSelectedEndcapVariantId()) {
          endcapEl.showError?.('Please select an Endcap');
          endcapEl.scrollIntoView?.({ behavior: 'smooth', block: 'center' });
          endcapEl.select?.focus?.();
          return;
        }
      }

      if (theme.settings.vibrateOnATC && window.navigator.vibrate) {
        window.navigator.vibrate(100);
      }

      this.errorMsg = null;
      this.setErrorMsgState();

      // Disable "Add to Cart" button until submission is complete.
      this.submitBtn.setAttribute('aria-disabled', 'true');
      this.submitBtn.classList.add('is-loading');

      const formData = new FormData(this.form);

      // FL-93: the Notify Me pre-order button adds "Expected availability: Coming soon" (its
      // offer has no date). Send the product's Stock Note instead, as the pre-order box shows,
      // so the basket, checkout and order carry the same date as the product page. Set on the
      // FormData so both the single-item and the multi-item add below use it.
      const preorderNote = this.form.querySelector('.pdp-expected.is-preorder')?.dataset.preorderNote;
      if (preorderNote && formData.has('properties[Expected availability]')) {
        formData.set('properties[Expected availability]', preorderNote);
      }

      let sections = 'cart-icon-bubble';
      if (this.cartDrawer) {
        sections += `,${this.cartDrawer.closest('.shopify-section').id.replace('shopify-section-', '')}`;
      }

      try {
        // Fetch the current cart first: it seeds the next configuration sequence number
        // and is reused below to detect whether the added variant was already present.
        const oldCartResponse = await fetch(`${theme.routes.cart}.js`);
        if (!oldCartResponse.ok) throw new Error(oldCartResponse.status);
        const oldCartData = await oldCartResponse.json();

        const setNumber = this.getNextSetNumber(oldCartData.items);
        const { items, mainVariantId } = this.buildCartItems(formData, setNumber);
        const useMultiItemAdd = items.length > 1;

        let response;
        let data;

        if (useMultiItemAdd) {
          // One request for main + power source + endcap so the drawer paints complete.
          response = await fetch(theme.routes.cartAdd, {
            method: 'POST',
            headers: {
              Accept: 'application/json',
              'Content-Type': 'application/json',
              'X-Requested-With': 'XMLHttpRequest'
            },
            body: JSON.stringify({
              items,
              sections,
              sections_url: window.location.pathname
            })
          });
          data = await response.json();
        } else {
          formData.append('sections_url', window.location.pathname);
          formData.append('sections', sections);

          response = await fetch(theme.routes.cartAdd, {
            method: 'POST',
            headers: {
              Accept: 'application/javascript',
              'X-Requested-With': 'XMLHttpRequest'
            },
            body: formData
          });
          data = await response.json();
        }

        let error = typeof data.description === 'string' ? data.description : data.message;
        if (data.errors && typeof data.errors === 'object') {
          error = Object.entries(data.errors).map((item) => item[1].join(', '));
        }

        if (data.status) this.setErrorMsgState(error);

        if (!response.ok) throw new Error(response.status);

        const addedVariantId = mainVariantId || data.variant_id;

        if (theme.settings.afterAtc === 'page') {
          // Allow the tick animation to complete
          setTimeout(() => {
            window.location.href = theme.routes.cart;
          }, 300);
        } else {
          // Update cart icon count.
          ProductForm.updateCartIcon(data);

          // If item was added from Quick Add drawer, show "Added to cart" message.
          const quickAddDrawer = this.closest('quick-add-drawer');
          if (quickAddDrawer) quickAddDrawer.addedToCart();

          setTimeout(() => {
            // Update cart drawer contents.
            if (this.cartDrawer) {
              this.cartDrawer.renderContents(
                data,
                !quickAddDrawer && theme.settings.afterAtc === 'drawer'
              );
            } else if (window.location.pathname === theme.routes.cart) {
              const cartItems = document.querySelector('cart-items');
              if (cartItems) {
                if (cartItems.dataset.empty === 'true') {
                  window.location.reload();
                } else {
                  cartItems.refresh();
                }
              }
            }
          }, 700);
        }

        const itemInOldCart = oldCartData.items.filter(
          (item) => item.variant_id === addedVariantId
        )[0];

        // Check if product was already in the cart
        if (itemInOldCart) {
          this.dispatchEvent(new CustomEvent('on:line-item:change', {
            bubbles: true,
            detail: {
              variantId: addedVariantId,
              oldQuantity: itemInOldCart.quantity,
              newQuantity: (itemInOldCart.quantity === data.quantity)
                ? itemInOldCart.quantity : data.quantity
            }
          }));
        } else {
          this.dispatchEvent(new CustomEvent('on:cart:add', {
            bubbles: true,
            detail: {
              variantId: addedVariantId
            }
          }));
        }
      } catch (error) {
        console.log(error); // eslint-disable-line
        this.dispatchEvent(new CustomEvent('on:cart:error', {
          bubbles: true,
          detail: {
            error: this.errorMsg?.textContent
          }
        }));

        if (this.cartDrawer) this.cartDrawer.refresh(true);
      } finally {
        // Re-enable 'Add to Cart' button.
        this.submitBtn.classList.add('is-success');
        this.submitBtn.removeAttribute('aria-disabled');
        setTimeout(() => {
          this.submitBtn.classList.remove('is-loading');
          this.submitBtn.classList.remove('is-success');
        }, 1400);
      }
    }

    /**
     * Updates the cart icon count in the header.
     * @param {object} response - Response JSON.
     */
    static updateCartIcon(response) {
      const cartIconBubble = document.getElementById('cart-icon-bubble');
      if (cartIconBubble && response?.sections?.['cart-icon-bubble']) {
        cartIconBubble.innerHTML = response.sections['cart-icon-bubble'];
      }
    }

    /**
     * Shows/hides an error message.
     * @param {string} [error=false] - Error to show a message for.
     */
    setErrorMsgState(error = false) {
      this.errorMsg = this.errorMsg || this.querySelector('.js-form-error');
      if (!this.errorMsg) return;

      this.errorMsg.hidden = !error;
      if (error) {
        this.errorMsg.innerHTML = '';
        const errorArray = Array.isArray(error) ? error : [error];
        errorArray.forEach((err, index) => {
          if (index > 0) this.errorMsg.insertAdjacentHTML('beforeend', '<br>');
          this.errorMsg.insertAdjacentText('beforeend', err);
        });
      }
    }
  }

  customElements.define('product-form', ProductForm);
}
